import type { Inspection, InspectionInspector } from '@/types/Inspection.type';
import type { User } from '@/types/User';

type SubmitInspectorFields = Pick<Inspection, 'inspectedBy' | 'inspector'>;

export type StampSubmittingInspectorParams<T extends SubmitInspectorFields> = {
  inspection: T;
  user: Pick<User, 'id' | 'name' | 'email'> | null | undefined;
  isAdministrator: boolean;
};

function buildInspector(
  user: Pick<User, 'id' | 'name' | 'email'>,
  existing: InspectionInspector | null | undefined
): InspectionInspector {
  const isSameInspector = existing?.id?.toLowerCase() === user.id.toLowerCase();
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    ...(isSameInspector && existing?.code ? { code: existing.code } : {}),
  };
}

/**
 * Assigns the submitting user as `inspectedBy` / `inspector` so PUT /inspections/sync/:id
 * records who submitted. Administrators keep the existing assignment, matching the start flow.
 */
export function stampSubmittingInspector<T extends SubmitInspectorFields>(
  params: StampSubmittingInspectorParams<T>
): T {
  const { inspection, user, isAdministrator } = params;
  if (isAdministrator || !user?.id) {
    return inspection;
  }
  return {
    ...inspection,
    inspectedBy: user.id,
    inspector: buildInspector(user, inspection.inspector),
  };
}
